import React from 'react';
import { StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { LEARN_LESSON_COUNT, LEARN_MODULES } from '@/constants/learnCurriculum';
import { PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb } from './landingTokens';

const HIGHLIGHTS = [
  { icon: 'circle-play', text: 'Short videos show you each step.' },
  { icon: 'clock', text: 'A few minutes each, at your own pace.' },
  { icon: 'seedling', text: 'No experience needed, and always free.' },
];

function LearnModulesPreview() {
  const { c, isXS } = useLanding();
  return (
    <View style={{ padding: isXS ? 12 : 24, borderRadius: 24, backgroundColor: c.accentSoft }}>
      <View
        accessibilityLabel="Gab AI Learn modules"
        style={{ borderRadius: 12, borderWidth: 1, borderColor: c.ruleStrong, backgroundColor: c.cardSolid, overflow: 'hidden', ...(isWeb ? { boxShadow: c.shadow } : {}) }}
      >
        <View style={[styles.header, { backgroundColor: c.panel, borderBottomColor: c.rule }]}>
          <Text weight="medium" style={{ color: c.ink, fontSize: 13 }}>Gab AI Learn</Text>
          <Text style={{ color: c.inkSoft, fontSize: 12 }}>{LEARN_LESSON_COUNT} lessons · {LEARN_MODULES.length} modules</Text>
        </View>
        {LEARN_MODULES.map((module, index) => (
          <View key={module.id} style={[styles.module, { borderTopColor: c.rule, borderTopWidth: index ? 1 : 0 }]}>
            <Text weight="medium" style={{ color: c.accent, fontSize: 12, width: 22 }}>{module.number}</Text>
            <Text weight="medium" numberOfLines={1} style={{ flex: 1, minWidth: 0, color: c.ink, fontSize: 14 }}>{module.title}</Text>
            <Text style={{ color: c.inkSoft, fontSize: 12 }}>{module.lessons.length} {module.lessons.length === 1 ? 'lesson' : 'lessons'}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function LandingLearn() {
  const { c, width, isXS, isCompact } = useLanding();
  const headingSize = fluid(width, 34, 48);

  return (
    <Section
      label="Gab AI Learn"
      style={{ borderTopWidth: 1, borderTopColor: c.rule }}
      contentStyle={{ paddingTop: fluid(width, 48, 80), paddingBottom: fluid(width, 48, 80) }}
    >
      <View style={{ flexDirection: isCompact ? 'column' : 'row', alignItems: isCompact ? 'stretch' : 'center', gap: isCompact ? 32 : 64 }}>
        <View style={{ flex: isCompact ? undefined : 1, minWidth: 0 }}>
          <View style={[styles.row, { marginBottom: 18 }]}>
            <Icon name="book-open" size={16} color={c.accent} />
            <Text weight="medium" style={{ color: c.accent, fontSize: 15 }}>Gab AI Learn</Text>
          </View>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: headingSize, lineHeight: Math.round(headingSize * 1.1), letterSpacing: -headingSize * 0.035, ...(isWeb ? { whiteSpace: 'pre-line' } : {}) }}>
            {'New to AI?\nStart here.'}
          </Text>
          <Text tag="p" style={{ color: c.inkSoft, fontSize: 17, lineHeight: 28, marginTop: 20, maxWidth: 460 }}>
            Free, practical lessons that teach you to use AI for everyday work, from your first prompt to handing off whole projects in Work.
          </Text>
          <View style={{ gap: 14, marginTop: 24, marginBottom: 28 }}>
            {HIGHLIGHTS.map(item => (
              <View key={item.icon} style={styles.row}>
                <Icon name={item.icon} size={15} color={c.accent} />
                <Text style={{ flex: 1, color: c.inkSoft, fontSize: 15, lineHeight: 23 }}>{item.text}</Text>
              </View>
            ))}
          </View>
          <PrimaryCta label="Start learning free" href="/learn" restrained full={isXS} />
        </View>
        <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
          <LearnModulesPreview />
        </View>
      </View>
    </Section>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1 },
  module: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 11 },
});

export default React.memo(LandingLearn);
